/**
 * Formatage et petites maths de la vue de combat — fonctions pures, aucune
 * dépendance DOM (testables en unitaire).
 */
import type { ThrowResult } from '../core/types';
// Libellés d'effets du bestiaire (contrat v0.8.0) : c'est le cœur qui les
// rédige, la vue ne fait que les afficher sous le nom de l'ennemi.
import { effectLabel } from '../data/enemies';

/** Au-delà de ce seuil, les nombres sont abrégés en k/M (COMMENTS §6). */
const ABBREVIATE_THRESHOLD = 10_000;

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function clamp01(v: number): number {
  return clamp(v, 0, 1);
}

/**
 * Taille de police (unités de conception) proportionnelle aux dégâts —
 * spec V0 §9. Valeurs ×1,5 par rapport au MVP 240×426 (résolution portée à
 * 360×640, DECISIONS §D.7).
 */
export function sizeForDamage(damage: number, crit: boolean): number {
  const base = 11 + Math.min(15, Math.log2(Math.max(1, damage)) * 2.7);
  return crit ? base * 1.35 : base;
}

/**
 * Un chiffre après la virgule (arrondi), séparateur français ; abrège en k/M
 * au-delà de {@link ABBREVIATE_THRESHOLD}. Partagé par {@link formatDamage}
 * (toujours une décimale) et {@link formatCount} (décimale seulement si le
 * nombre n'est pas entier).
 */
function formatAbbreviated(value: number, alwaysDecimal: boolean): string {
  const v = Math.max(0, value);
  if (v >= 1_000_000) return formatBig(v);
  if (v >= ABBREVIATE_THRESHOLD) return `${oneDecimal(v / 1000, true)}k`;
  return oneDecimal(v, alwaysDecimal);
}

/**
 * Suffixes des grands nombres (A77, v0.30.1) : échelle longue française —
 * M (million, 10⁶), Md (milliard, 10⁹), Bn (billion, 10¹²), Bd (billiard,
 * 10¹⁵), Tn (trillion, 10¹⁸), Td (trilliard, 10²¹), Qa (quadrillion, 10²⁴),
 * Qd (quadrilliard, 10²⁷), Qi (quintillion, 10³⁰). Au-delà, notation
 * scientifique à une décimale (« 2,3e37 »).
 */
const BIG_SUFFIXES: readonly string[] = ['M', 'Md', 'Bn', 'Bd', 'Tn', 'Td', 'Qa', 'Qd', 'Qi'];

/**
 * Nombre ≥ 10⁶ abrégé avec une décimale et son suffixe (« 1,2M », « 3,4Bn »,
 * « 2,3e37 »). Sous le million, une décimale sans suffixe. `NaN` et les
 * infinis donnent « — ».
 */
export function formatBig(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const v = Math.max(0, value);
  if (v < 1_000_000) return oneDecimal(v, true);
  let scaled = v / 1_000_000;
  for (const suffix of BIG_SUFFIXES) {
    if (scaled < 999.95) return `${oneDecimal(scaled, true)}${suffix}`;
    scaled /= 1000;
  }
  return v.toExponential(1).replace('.', ',').replace('e+', 'e');
}

function oneDecimal(value: number, force: boolean): string {
  const rounded = Math.round(value * 10) / 10;
  const s = !force && Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return s.replace('.', ',');
}

/**
 * Dégâts : toujours un chiffre après la virgule (COMMENTS §6, « Les dégâts
 * sont maintenant affichés avec un chiffre après la virgule ») — `3,5`,
 * `12,0`. Abrégé en k/M au-delà de 10 000 (`1,2k`).
 */
export function formatDamage(value: number): string {
  return formatAbbreviated(value, true);
}

/**
 * Compteur (PV…) : une décimale seulement si le nombre n'est pas entier —
 * `18`, `12,5`. Mêmes abréviations k/M que {@link formatDamage} au-delà du
 * même seuil.
 */
export function formatCount(value: number): string {
  return formatAbbreviated(value, false);
}

/**
 * Libellé nom + PV affiché au-dessus de la barre de vie de l'ennemi
 * (COMMENTS §6) : « Gobelin  12,5 / 18 ».
 */
export function formatEnemyLabel(name: string, hp: number, maxHp: number): string {
  return `${name}  ${formatCount(hp)} / ${formatCount(maxHp)}`;
}

/** Multiplicateur de sort : « 1,25 », « 1,75 »… (virgule, zéros inutiles retirés). */
export function formatMult(value: number): string {
  let s = value.toFixed(2);
  while (s.includes('.') && s.endsWith('0')) s = s.slice(0, -1);
  if (s.endsWith('.')) s = s.slice(0, -1);
  return s.replace('.', ',');
}

export function formatSeconds(value: number): string {
  return value % 1 === 0 ? String(value) : value.toFixed(1).replace('.', ',');
}

// ────────────────────────────────────── Sorts (v0.5.0 — slots/dés multiples)

/**
 * Sort déclenché, tel qu'exposé par le cœur (`ThrowResult.spells: TriggeredSpell[]`,
 * COMMENTS v0.4.1 §E A9 — plusieurs sorts par lancer : au plus un sort
 * d'emplacement, tous les sorts de dé distincts qui correspondent). Alias
 * **structurel** plutôt qu'un import direct de `TriggeredSpell` : tous les
 * champs sauf `id` restent optionnels ici, pour que la vue de combat continue
 * de fonctionner si le cœur en fait évoluer la forme (label absent, champ
 * renommé…) sans casser au typecheck.
 */
export interface TriggeredSpellLike {
  id: string;
  kind?: 'slot' | 'die';
  multiplier?: number;
  bonusSeconds?: number;
  goldMult?: number;
  label?: string;
}

/** Libellés français de repli quand le cœur ne fournit pas `label` (COMMENTS v0.4.1 §E A3/A9). */
const SPELL_LABELS: Record<string, string> = {
  // Sorts d'emplacement.
  pair: 'Paire',
  snakeEyes: 'Yeux du serpent',
  triple: 'Brelan',
  sixes: '666',
  doublePair: 'Double paire',
  four: 'Carré',
  straight: 'Suite',
  full: 'Full',
  // Sorts de dé.
  fortyTwo: '42',
  maxroll: 'Maxroll',
  pi: 'Pi',
  parity: 'Parité',
  goldenRatio: "Nombre d'or",
  primes: 'Premiers',
};

/** Libellé français d'un sort : `label` du cœur si fourni, sinon la table ci-dessus, sinon l'id brut. */
export function spellLabel(spell: TriggeredSpellLike): string {
  return spell.label || SPELL_LABELS[spell.id] || spell.id;
}

/**
 * Texte de bannière d'un sort — v0.5.0 : « Paire ×1,25 », « Pi ×3,14 »,
 * « 42 +3 s », « Nombre d'or ×1,61 or ». N'affiche que les grandeurs non
 * neutres du sort (multiplicateur ≠ 1, or ≠ 1, secondes ≠ 0) : un sort donné
 * n'en affecte en général qu'une seule, plusieurs s'accumulent sur la même
 * ligne si besoin.
 */
export function newSpellBannerText(spell: TriggeredSpellLike): string {
  const parts: string[] = [];
  if (typeof spell.multiplier === 'number' && spell.multiplier !== 1) {
    parts.push(`×${formatMult(spell.multiplier)}`);
  }
  if (typeof spell.goldMult === 'number' && spell.goldMult !== 1) {
    parts.push(`×${formatMult(spell.goldMult)} or`);
  }
  if (typeof spell.bonusSeconds === 'number' && spell.bonusSeconds !== 0) {
    parts.push(`+${formatSeconds(spell.bonusSeconds)} s`);
  }
  const label = spellLabel(spell);
  return parts.length > 0 ? `${label} ${parts.join(' ')}` : label;
}

/** `true` si le cœur expose `ThrowResult.spells` sous forme de tableau (même vide). */
export function hasSpellsField(result: ThrowResult): boolean {
  return Array.isArray((result as { spells?: unknown }).spells);
}

/**
 * Sorts déclenchés par un lancer, lus sur `ThrowResult.spells` — vérifier
 * {@link hasSpellsField} d'abord : la vue de combat garde par ailleurs un
 * repli sur l'ancien `ThrowResult.spell` unique (`battleView.ts`) pour rester
 * correcte si jamais `spells` venait à disparaître d'une version du cœur.
 */
export function spellsOf(result: ThrowResult): TriggeredSpellLike[] {
  const spells = (result as { spells?: TriggeredSpellLike[] }).spells;
  return Array.isArray(spells) ? spells : [];
}

// ───────────────── Champs v0.7.0 du cœur, lus en **détection de présence**
//
// La vue de combat et le cœur avancent en parallèle : chacun de ces champs est
// lu défensivement (type vérifié, valeur de repli neutre), pour que la vue
// reste correcte aussi bien avant qu'après leur arrivée.

/** `state.minionEnabled` (A18) : le Larbin lance à la place du joueur. */
export function minionEnabledOf(state: unknown): boolean {
  return (state as { minionEnabled?: unknown } | null)?.minionEnabled === true;
}

/** `state.combat.inferno` (666) : le sol brûle jusqu'à la fin de l'Expédition. */
export function infernoOf(state: unknown): boolean {
  const combat = (state as { combat?: { inferno?: unknown } } | null)?.combat;
  return combat?.inferno === true;
}

/**
 * `state.combat.primesQueue` : multiplicateurs des prochains lancers du sort
 * *Premiers* (typiquement `[2, 3, 5]`, qui se vide lancer après lancer).
 * `null` tant que le cœur n'expose pas le champ — la vue n'affiche alors aucun
 * jeton.
 */
export function primesQueueOf(state: unknown): number[] | null {
  const combat = (state as { combat?: { primesQueue?: unknown } } | null)?.combat;
  const queue = combat?.primesQueue;
  if (!Array.isArray(queue)) return null;
  const out: number[] = [];
  for (const v of queue) if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
  return out;
}

/**
 * Charge utile de l'événement `'minionThrow'` → `ThrowResult`. Le cœur peut
 * l'émettre tel quel (`ThrowResult`) ou enveloppé (`{ result }`) : les deux
 * formes sont acceptées, tout le reste renvoie `null` (l'événement est ignoré).
 */
export function throwResultOf(payload: unknown): ThrowResult | null {
  const direct = payload as { dice?: unknown; result?: unknown } | null;
  if (!direct) return null;
  if (Array.isArray(direct.dice)) return payload as ThrowResult;
  const wrapped = direct.result as { dice?: unknown } | undefined;
  if (wrapped && Array.isArray(wrapped.dice)) return wrapped as ThrowResult;
  return null;
}

// ───────────────── Champs v0.8.0 du cœur, lus en **détection de présence**
//
// Effets d'ennemis (contrat v0.8.0, COMMENTS v0.7.0 §1) : tant que le cœur ne
// les expose pas, chaque lecture retombe sur une valeur neutre et la vue se
// comporte exactement comme en v0.7.0.

/** `state.stunRemaining` (A29) : le joueur est assommé, aucun lancer possible. */
export function isStunnedOf(state: unknown): boolean {
  const remaining = (state as { stunRemaining?: unknown } | null)?.stunRemaining;
  return typeof remaining === 'number' && remaining > 0;
}

/**
 * `game.blockedSlots()` : emplacements de dés Maîtres retirés du lancer par
 * l'ennemi courant (Orc Mage 1, Licorne sadique 3). `0` sans la méthode.
 */
export function blockedSlotsOf(game: unknown): number {
  const fn = (game as { blockedSlots?: unknown } | null)?.blockedSlots;
  if (typeof fn !== 'function') return 0;
  try {
    const n = (fn as () => unknown).call(game);
    return typeof n === 'number' && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

/** `enemy.effect.kind` : famille d'effet de l'ennemi courant, `''` s'il n'en a pas. */
export function enemyEffectKind(enemy: unknown): string {
  const effect = (enemy as { effect?: { kind?: unknown } | null } | null)?.effect;
  return typeof effect?.kind === 'string' ? effect.kind : '';
}

/** Valeur numérique d'un champ de l'effet de l'ennemi (`maxHits`, `factor`…). */
export function enemyEffectNumber(enemy: unknown, field: string): number {
  const effect = (enemy as { effect?: Record<string, unknown> | null } | null)?.effect;
  const value = effect?.[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** `enemy.hits` (A28) : coups reçus, pour le compteur du Serpent. */
export function enemyHitsOf(enemy: unknown): number {
  const hits = (enemy as { hits?: unknown } | null)?.hits;
  return typeof hits === 'number' && hits > 0 ? Math.floor(hits) : 0;
}

/** `ApplyResult.dodged` (A29) : le lancer entier a été esquivé. */
export function dodgedOf(payload: unknown): boolean {
  return (payload as { dodged?: unknown } | null)?.dodged === true;
}

/** `ApplyResult.expeditionLost` (A28) : ce lancer a fait perdre l'Expédition. */
export function expeditionLostOf(payload: unknown): boolean {
  return (payload as { expeditionLost?: unknown } | null)?.expeditionLost === true;
}

/**
 * Longueur maximale du libellé d'effet affiché sous le nom de l'ennemi : au
 *-delà, il est coupé par une ellipse. 40 caractères, c'est ce qui tient sur
 * une ligne des 360 unités du cadre en police pixel (le HUD DOM, lui, affiche
 * le libellé entier — `src/ui`).
 */
const EFFECT_LABEL_MAX = 40;

/**
 * Libellé court de l'effet de l'ennemi, via `effectLabel()` de
 * `src/data/enemies.ts` (contrat v0.8.0), tronqué à {@link EFFECT_LABEL_MAX}.
 * Chaîne **vide** si l'ennemi n'a pas d'effet : la vue n'affiche alors aucune
 * ligne sous son nom, exactement comme en v0.7.0.
 */
export function effectLabelOf(enemy: unknown): string {
  const effect = (enemy as { effect?: unknown } | null)?.effect;
  if (!effect) return '';
  try {
    const label = effectLabel(effect as Parameters<typeof effectLabel>[0]);
    if (typeof label !== 'string' || label.length === 0) return '';
    return label.length > EFFECT_LABEL_MAX ? `${label.slice(0, EFFECT_LABEL_MAX - 1)}…` : label;
  } catch {
    // Un effet d'une forme inattendue ne doit jamais faire tomber la vue.
    return '';
  }
}

/** Secondes portées par un événement (`stun`, `timeDrain`), `0` par défaut. */
export function secondsOf(payload: unknown): number {
  const seconds = (payload as { seconds?: unknown } | null)?.seconds;
  return typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 0;
}

/** Interpolation linéaire, utilisée pour lisser la barre de PV. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function easeOutCubic(t: number): number {
  const p = 1 - t;
  return 1 - p * p * p;
}

/** Courbe de Bézier quadratique 1D (arc de vol des dés). */
export function bezier(p0: number, p1: number, p2: number, t: number): number {
  const u = 1 - t;
  return u * u * p0 + 2 * u * t * p1 + t * t * p2;
}

/** Screen shake à décroissance linéaire (appliqué au container racine). */
export class Shake {
  private intensity = 0;
  private duration = 0;
  private elapsed = 0;
  x = 0;
  y = 0;

  trigger(amount: number, durationMs: number): void {
    if (amount <= this.intensity && this.elapsed < this.duration) return;
    this.intensity = amount;
    this.duration = Math.max(1, durationMs) / 1000;
    this.elapsed = 0;
  }

  update(dt: number): void {
    if (this.elapsed >= this.duration) {
      this.x = 0;
      this.y = 0;
      return;
    }
    this.elapsed += dt;
    const remaining = Math.max(0, 1 - this.elapsed / this.duration);
    const amp = this.intensity * remaining;
    this.x = (Math.random() * 2 - 1) * amp;
    this.y = (Math.random() * 2 - 1) * amp;
  }
}

/**
 * Champs du critique global (COMMENTS §8) : le core calcule désormais le
 * critique **une fois pour tout le lancer** plutôt que dé par dé.
 */
export interface ThrowCritFields {
  crit: boolean;
  critMult: number;
  spellMult: number;
  subtotal: number;
}

/**
 * Lit les champs de critique global s'ils existent, `undefined` sinon.
 * Volontairement pas un *type predicate* (`result is …`) : `ThrowResult`
 * déclare déjà ces champs en dur côté core, ce qui ferait dégénérer la
 * branche négative en `never` — un simple cast défensif marche dans les deux
 * cas (champs présents ou pas encore) sans dépendre de l'état du type.
 */
export function critFieldsOf(result: ThrowResult): Partial<ThrowCritFields> {
  return result as unknown as Partial<ThrowCritFields>;
}

/**
 * Vrai si `result` porte les nouveaux champs de critique global. Permet à la
 * vue de basculer sur le rendu §8 (total flottant, style critique unique) et
 * de retomber sur l'ancien rendu dé-par-dé tant que le core ne les expose pas
 * — la vue de combat reste correcte quel que soit l'état d'avancement du core.
 */
export function hasCritFields(result: ThrowResult): boolean {
  return typeof critFieldsOf(result).crit === 'boolean';
}

/** `navigator.vibrate`, jamais levé si absent ou refusé (Safari/iOS notamment). */
export function vibrate(pattern: number | number[]): void {
  try {
    const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
    nav.vibrate?.(pattern);
  } catch {
    /* silencieux : la vibration est un bonus, jamais un pré-requis. */
  }
}
